import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateTime } from 'luxon';
import { take } from 'rxjs';
import { APIResponse } from 'fakturnia-shared';
import { FakturniaHighlightComponent } from '@fakturnia/components/highlight';
import { SubscriptionInvoice, SubscriptionsApiService } from 'shared/services/api/subscriptions.api.service';
import { SnackbarService } from 'shared/services/snackbar.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    FakturniaHighlightComponent
  ],
  selector: 'app-ksef-subscription-invoice-dialog',
  templateUrl: './ksef-subscription-invoice-dialog.component.html',
  styleUrls: ['./ksef-subscription-invoice-dialog.component.scss']
})
export class KsefSubscriptionInvoiceDialogComponent {
  
  isFetchingXml = false;
  isFetchingUpo = false;
  dataUpdated = false; // Flaga czy dane zostały zaktualizowane

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { invoice: SubscriptionInvoice },
    private dialogRef: MatDialogRef<KsefSubscriptionInvoiceDialogComponent>,
    private _subscriptionsApi: SubscriptionsApiService,
    private _snackbar: SnackbarService
  ) {}

  get invoice(): SubscriptionInvoice {
    return this.data.invoice;
  }

  formatDate(date: string | Date | undefined): string {
    if (!date) return '-';
    const dt = typeof date === 'string' ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
    return dt.toFormat('dd.MM.yyyy HH:mm:ss');
  }

  formatDateShort(date: string | Date | undefined): string {
    if (!date) return '-';
    const dt = typeof date === 'string' ? DateTime.fromISO(date) : DateTime.fromJSDate(date);
    return dt.toFormat('dd.MM.yyyy');
  }

  getStatusCode(): number {
    return this.invoice.ksefStatus?.code || 0;
  }

  getStatusDescription(): string {
    return this.invoice.ksefStatus?.description || '-';
  }

  getStatusClass(): string {
    const code = this.getStatusCode();
    if (code === 200) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (code >= 400) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }

  /**
   * Getter dla sformatowanego XML z większymi wcięciami
   */
  get formattedXml(): string {
    if (!this.invoice.ksefXml) return '';
    
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(this.invoice.ksefXml, 'text/xml');
      
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        return this.invoice.ksefXml;
      }
      
      return this.formatXml(xmlDoc);
    } catch (e) {
      console.error('Błąd formatowania XML:', e);
      return this.invoice.ksefXml;
    }
  }

  /**
   * Getter dla sformatowanego UPO XML
   */
  get formattedUpo(): string {
    if (!this.invoice.ksefUpo) return '';
    
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(this.invoice.ksefUpo, 'text/xml');
      
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        return this.invoice.ksefUpo;
      }
      
      return this.formatXml(xmlDoc);
    } catch (e) {
      console.error('Błąd formatowania UPO:', e);
      return this.invoice.ksefUpo;
    }
  }

  private formatXml(xml: Document | Element, level: number = 0): string {
    const indent = '    '; // 4 spacje
    const padding = indent.repeat(level);
    let formatted = '';

    if (xml instanceof Document) {
      formatted += '<?xml version="1.0" encoding="UTF-8"?>\n';
      if (xml.documentElement) {
        formatted += this.formatXml(xml.documentElement, level);
      }
      return formatted;
    }

    const element = xml as Element;
    const tagName = element.tagName;
    const attributes = element.attributes;
    const children = element.childNodes;

    formatted += padding + '<' + tagName;

    for (let i = 0; i < attributes.length; i++) {
      const attr = attributes[i];
      formatted += ' ' + attr.name + '="' + attr.value + '"';
    }

    const hasElementChildren = Array.from(children).some(child => child.nodeType === 1);
    const textContent = element.textContent?.trim() || '';

    if (children.length === 0) {
      formatted += '/>\n';
    } else if (!hasElementChildren && textContent) {
      formatted += '>' + textContent + '</' + tagName + '>\n';
    } else {
      formatted += '>\n';

      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.nodeType === 1) {
          formatted += this.formatXml(child as Element, level + 1);
        }
      }

      formatted += padding + '</' + tagName + '>\n';
    }

    return formatted;
  }

  copyToClipboard(text: string | undefined, type: string) {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      console.log(`${type} skopiowany do schowka`);
    });
  }

  downloadXml() {
    if (!this.invoice.ksefXml) return;
    const blob = new Blob([this.invoice.ksefXml], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.invoice.documentNumber?.replace(/\//g, '_') || 'faktura'}_ksef.xml`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  downloadUpo() {
    if (!this.invoice.ksefUpo) return;
    const blob = new Blob([this.invoice.ksefUpo], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.invoice.documentNumber?.replace(/\//g, '_') || 'faktura'}_upo.xml`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  close() {
    this.dialogRef.close({ updated: this.dataUpdated });
  }

  /**
   * Pobierz XML faktury z KSeF (manualne)
   */
  fetchXmlFromKsef() {
    if (this.isFetchingXml || !this.invoice._id || !this.invoice.ksefNumber) return;
    
    this.isFetchingXml = true;
    this._subscriptionsApi.fetchInvoiceXmlFromKsef(this.invoice._id).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isFetchingXml = false;
        if (response.success && response.data?.xml) {
          this.data.invoice.ksefXml = response.data.xml;
          this.dataUpdated = true;
          this._snackbar.showSnackBar('XML faktury pobrany pomyślnie', 'success');
        } else {
          this._snackbar.showSnackBar(response.message || 'Nie udało się pobrać XML', 'warning');
        }
      },
      error: (error) => {
        this.isFetchingXml = false;
        this._snackbar.showSnackBar('Błąd podczas pobierania XML z KSeF', 'warning');
        console.error('Fetch XML error:', error);
      }
    });
  }

  /**
   * Pobierz UPO faktury z KSeF (manualne)
   */
  fetchUpoFromKsef() {
    if (this.isFetchingUpo || !this.invoice._id || !this.invoice.ksefNumber) return;
    
    this.isFetchingUpo = true;
    this._subscriptionsApi.fetchInvoiceUpoFromKsef(this.invoice._id).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isFetchingUpo = false;
        if (response.success && response.data?.upo) {
          this.data.invoice.ksefUpo = response.data.upo;
          this.data.invoice.ksefUpoTimestamp = response.data.upoTimestamp;
          this.dataUpdated = true;
          this._snackbar.showSnackBar('UPO pobrane pomyślnie', 'success');
        } else {
          this._snackbar.showSnackBar(response.message || 'Nie udało się pobrać UPO', 'warning');
        }
      },
      error: (error) => {
        this.isFetchingUpo = false;
        this._snackbar.showSnackBar('Błąd podczas pobierania UPO z KSeF', 'warning');
        console.error('Fetch UPO error:', error);
      }
    });
  }
}
